import { useState, useRef, useEffect } from 'react';
import { VoteButtons } from './VoteButtons';
import { BACKEND_URL } from '@/config/api';

export const WhiteboardObject = ({
  object,
  isSelected,
  zoom,
  snapToGrid,
  gridSize,
  onSelect,
  onUpdate,
  onUpdateWithHistory,
  onContextMenu,
  whiteboardId,
  currentUserId,
  token,
  onVoteUpdate,
  showVoting = true,
  onDragStart,
  onDragEnd,
  alignmentGuides,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(object.text || '');
  const dragStart = useRef({ x: 0, y: 0, objX: 0, objY: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const textRef = useRef(null);

  // Snap to grid helper
  const snap = (value) => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  // Handle drag start
  const handleMouseDown = (e) => {
    if (object.locked || isEditing) return;
    e.stopPropagation();
    
    onSelect(e);
    
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX / zoom,
      y: e.clientY / zoom,
      objX: object.x,
      objY: object.y,
    };
    
    // Notify parent that dragging has started for alignment guides
    onDragStart?.({ ...object });
  };

  // Handle double click for editing
  const handleDoubleClick = (e) => {
    if (object.locked) return;
    e.stopPropagation();
    
    if (object.type === 'sticky' || object.type === 'text' || object.type === 'shape') {
      setIsEditing(true);
      setEditText(object.text || '');
    }
  };

  // Handle resize start
  const handleResizeStart = (e, handle) => {
    if (object.locked) return;
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeHandle(handle);
    resizeStart.current = {
      x: e.clientX / zoom,
      y: e.clientY / zoom,
      width: object.width,
      height: object.height,
      objX: object.x,
      objY: object.y,
    };
  };

  // Handle mouse move for drag/resize
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      const currentX = e.clientX / zoom;
      const currentY = e.clientY / zoom;

      if (isDragging) {
        const dx = currentX - dragStart.current.x;
        const dy = currentY - dragStart.current.y;
        
        let newX = snapToGrid ? snap(dragStart.current.objX + dx) : dragStart.current.objX + dx;
        let newY = snapToGrid ? snap(dragStart.current.objY + dy) : dragStart.current.objY + dy;
        
        // Apply alignment snapping if available
        if (alignmentGuides) {
          if (alignmentGuides.snapX !== null) newX = alignmentGuides.snapX;
          if (alignmentGuides.snapY !== null) newY = alignmentGuides.snapY;
        }
        
        onUpdate(object.id, { x: newX, y: newY });
        
        // Update dragged object state for alignment guides calculation
        onDragStart?.({ ...object, x: newX, y: newY });
      } else if (isResizing) {
        const dx = currentX - resizeStart.current.x;
        const dy = currentY - resizeStart.current.y;
        
        let newWidth = resizeStart.current.width;
        let newHeight = resizeStart.current.height;
        let newX = resizeStart.current.objX;
        let newY = resizeStart.current.objY;

        if (resizeHandle.includes('e')) {
          newWidth = Math.max(50, resizeStart.current.width + dx);
        }
        if (resizeHandle.includes('w')) {
          newWidth = Math.max(50, resizeStart.current.width - dx);
          newX = resizeStart.current.objX + dx;
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(30, resizeStart.current.height + dy);
        }
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(30, resizeStart.current.height - dy);
          newY = resizeStart.current.objY + dy;
        }

        onUpdate(object.id, {
          x: snap(newX),
          y: snap(newY),
          width: snap(newWidth),
          height: snap(newHeight),
        });
      }
    };

    const handleMouseUp = () => {
      if (isDragging || isResizing) {
        onUpdateWithHistory(object.id, {
          x: object.x,
          y: object.y,
          width: object.width,
          height: object.height,
        });
      }
      if (isDragging) {
        onDragEnd?.(); // Notify parent that dragging has ended
      }
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, resizeHandle, object, zoom, snap, snapToGrid, onUpdate, onUpdateWithHistory, alignmentGuides, onDragStart, onDragEnd]);

  // Handle text edit
  useEffect(() => {
    if (isEditing && textRef.current) {
      textRef.current.focus();
      textRef.current.select();
    }
  }, [isEditing]);

  const handleTextBlur = () => {
    setIsEditing(false);
    if (editText !== object.text) {
      onUpdateWithHistory(object.id, { text: editText });
    }
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditText(object.text || '');
    } else if (e.key === 'Enter' && !e.shiftKey && object.type === 'text') {
      // Only single-line text objects finish editing on Enter
      // Shapes and sticky notes allow multi-line (Enter creates new line)
      handleTextBlur();
    }
  };

  // Render resize handles
  const renderResizeHandles = () => {
    if (!isSelected || object.locked) return null;
    
    const handles = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
    const handleSize = 8;
    
    return handles.map(handle => {
      let x, y;
      
      switch (handle) {
        case 'nw': x = -handleSize / 2; y = -handleSize / 2; break;
        case 'ne': x = object.width - handleSize / 2; y = -handleSize / 2; break;
        case 'sw': x = -handleSize / 2; y = object.height - handleSize / 2; break;
        case 'se': x = object.width - handleSize / 2; y = object.height - handleSize / 2; break;
        case 'n': x = object.width / 2 - handleSize / 2; y = -handleSize / 2; break;
        case 's': x = object.width / 2 - handleSize / 2; y = object.height - handleSize / 2; break;
        case 'e': x = object.width - handleSize / 2; y = object.height / 2 - handleSize / 2; break;
        case 'w': x = -handleSize / 2; y = object.height / 2 - handleSize / 2; break;
        default: return null;
      }
      
      const cursors = {
        nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
        n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize'
      };
      
      return (
        <rect
          key={handle}
          x={x}
          y={y}
          width={handleSize}
          height={handleSize}
          fill="white"
          stroke="#2563EB"
          strokeWidth={1.5}
          style={{ cursor: cursors[handle] }}
          onMouseDown={(e) => handleResizeStart(e, handle)}
        />
      );
    });
  };

  // Render object based on type
  const renderObject = () => {
    switch (object.type) {
      case 'sticky':
        return (
          <g>
            <rect
              width={object.width}
              height={object.height}
              fill={object.color}
              rx={4}
              filter="url(#shadow)"
            />
            {isEditing ? (
              <foreignObject width={object.width} height={object.height}>
                <textarea
                  ref={textRef}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={handleTextBlur}
                  onKeyDown={handleTextKeyDown}
                  className="w-full h-full p-3 bg-transparent resize-none outline-none text-sm"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    color: object.textColor || '#1E293B'
                  }}
                />
              </foreignObject>
            ) : (
              <text
                x={12}
                y={24}
                fontSize={14}
                fill={object.textColor || '#1E293B'}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {(object.text || '').split('\n').map((line, i) => (
                  <tspan key={i} x={12} dy={i === 0 ? 0 : 18}>{line}</tspan>
                ))}
              </text>
            )}
            {/* Vote buttons for sticky notes - right edge */}
            {showVoting && isSelected && !isEditing && whiteboardId && (
              <foreignObject 
                x={object.width + 8} 
                y={(object.height - 80) / 2} 
                width={44} 
                height={80}
                style={{ overflow: 'visible' }}
              >
                <div 
                  className="flex flex-col items-center gap-1 bg-white rounded-xl shadow-lg border-2 border-gray-200 p-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={async (e) => { 
                      e.stopPropagation(); 
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/objects/${object.id}/vote`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ vote_type: 'up' })
                        });
                        if (response.ok) {
                          const contentType = response.headers.get('content-type');
                          if (contentType && contentType.includes('application/json')) {
                            const data = await response.json();
                            onVoteUpdate?.(object.id, data.votes);
                          }
                        } else {
                          console.error('Vote failed:', response.status);
                        }
                      } catch (err) {
                        console.error('Vote error:', err);
                      }
                    }}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                      object.votes?.up?.includes(currentUserId) 
                        ? 'bg-green-500 text-white shadow-md' 
                        : 'bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-600'
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={object.votes?.up?.includes(currentUserId) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                    </svg>
                  </button>
                  <span className={`text-sm font-bold ${
                    ((object.votes?.up?.length || 0) - (object.votes?.down?.length || 0)) > 0 ? 'text-green-600' :
                    ((object.votes?.up?.length || 0) - (object.votes?.down?.length || 0)) < 0 ? 'text-red-600' : 'text-gray-400'
                  }`}>
                    {(object.votes?.up?.length || 0) - (object.votes?.down?.length || 0)}
                  </span>
                  <button
                    onClick={async (e) => { 
                      e.stopPropagation();
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/objects/${object.id}/vote`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ vote_type: 'down' })
                        });
                        if (response.ok) {
                          const contentType = response.headers.get('content-type');
                          if (contentType && contentType.includes('application/json')) {
                            const data = await response.json();
                            onVoteUpdate?.(object.id, data.votes);
                          }
                        } else {
                          console.error('Vote failed:', response.status);
                        }
                      } catch (err) {
                        console.error('Vote error:', err);
                      }
                    }}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                      object.votes?.down?.includes(currentUserId) 
                        ? 'bg-red-500 text-white shadow-md' 
                        : 'bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600'
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={object.votes?.down?.includes(currentUserId) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                    </svg>
                  </button>
                </div>
              </foreignObject>
            )}
            {/* Show vote count badge when not selected */}
            {showVoting && !isSelected && object.votes && (object.votes.up?.length > 0 || object.votes.down?.length > 0) && (
              <g transform={`translate(${object.width - 6}, ${object.height / 2 - 10})`}>
                <rect x={0} y={0} width={20} height={20} rx={4} fill={
                  (object.votes.up?.length || 0) - (object.votes.down?.length || 0) > 0 ? '#10B981' : 
                  (object.votes.up?.length || 0) - (object.votes.down?.length || 0) < 0 ? '#EF4444' : '#6B7280'
                } />
                <text x={10} y={14} textAnchor="middle" fill="white" fontSize={10} fontWeight="bold">
                  {(object.votes.up?.length || 0) - (object.votes.down?.length || 0)}
                </text>
              </g>
            )}
          </g>
        );

      case 'text':
        return (
          <g>
            {isEditing ? (
              <foreignObject width={Math.max(object.width, 200)} height={Math.max(object.height, 30)}>
                <input
                  ref={textRef}
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={handleTextBlur}
                  onKeyDown={handleTextKeyDown}
                  className="w-full h-full bg-transparent outline-none"
                  style={{ 
                    fontSize: object.fontSize || 16, 
                    fontWeight: object.fontWeight || 'normal',
                    color: object.color || '#1E293B',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </foreignObject>
            ) : (
              <text
                x={0}
                y={object.fontSize || 16}
                fontSize={object.fontSize || 16}
                fontWeight={object.fontWeight || 'normal'}
                fill={object.color || '#1E293B'}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {object.text}
              </text>
            )}
          </g>
        );

      case 'shape':
        const shape = object.shape || 'rectangle';
        const fillOpacity = object.fillOpacity ?? 1;
        let shapePath;
        
        switch (shape) {
          case 'rounded':
            shapePath = (
              <rect
                width={object.width}
                height={object.height}
                rx={12}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
              />
            );
            break;
          case 'circle':
            shapePath = (
              <ellipse
                cx={object.width / 2}
                cy={object.height / 2}
                rx={object.width / 2}
                ry={object.height / 2}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
              />
            );
            break;
          case 'diamond':
            shapePath = (
              <polygon
                points={`${object.width / 2},0 ${object.width},${object.height / 2} ${object.width / 2},${object.height} 0,${object.height / 2}`}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
              />
            );
            break;
          case 'hexagon':
            const hW = object.width;
            const hH = object.height;
            shapePath = (
              <polygon
                points={`${hW * 0.25},0 ${hW * 0.75},0 ${hW},${hH * 0.5} ${hW * 0.75},${hH} ${hW * 0.25},${hH} 0,${hH * 0.5}`}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
              />
            );
            break;
          case 'cloud':
            shapePath = (
              <ellipse
                cx={object.width / 2}
                cy={object.height / 2}
                rx={object.width / 2}
                ry={object.height / 2}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
                strokeDasharray={object.strokeWidth > 0 ? "8 4" : "0"}
              />
            );
            break;
          default: // rectangle
            shapePath = (
              <rect
                width={object.width}
                height={object.height}
                fill={object.fill}
                fillOpacity={fillOpacity}
                stroke={object.stroke}
                strokeWidth={object.strokeWidth}
              />
            );
        }
        
        return (
          <g>
            {shapePath}
            {isEditing ? (
              <foreignObject 
                width={object.width} 
                height={object.height} 
                x="0" 
                y="0"
                style={{ overflow: 'visible' }}
              >
                <div 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    boxSizing: 'border-box'
                  }}
                >
                  <textarea
                    ref={textRef}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onBlur={handleTextBlur}
                    onKeyDown={handleTextKeyDown}
                    placeholder="Type here..."
                    style={{ 
                      width: '100%',
                      height: '100%',
                      fontSize: object.fontSize || 14, 
                      fontFamily: 'Inter, sans-serif',
                      color: object.textColor || '#1E293B',
                      lineHeight: 1.4,
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      textAlign: 'center',
                      resize: 'none',
                    }}
                    autoFocus
                  />
                </div>
              </foreignObject>
            ) : object.text ? (
              <foreignObject 
                width={object.width} 
                height={object.height} 
                x="0" 
                y="0"
              >
                <div 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    fontSize: object.fontSize || 14, 
                    fontFamily: 'Inter, sans-serif',
                    color: object.textColor || '#1E293B',
                    lineHeight: 1.4,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {object.text}
                </div>
              </foreignObject>
            ) : null}
          </g>
        );

      case 'frame':
        return (
          <g>
            <rect
              width={object.width}
              height={object.height}
              fill="transparent"
              stroke={object.stroke}
              strokeWidth={object.strokeWidth}
              strokeDasharray="8 4"
              rx={8}
            />
            <text
              x={8}
              y={-8}
              fontSize={12}
              fill="#64748B"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {object.title}
            </text>
          </g>
        );

      case 'drawing':
        // Create smooth bezier path from points
        if (!object.points || object.points.length < 2) return null;
        
        const drawingOpacity = object.opacity || 1;
        const isHighlighter = object.isHighlighter || false;
        const drawStrokeWidth = isHighlighter ? (object.strokeWidth || 2) * 4 : (object.strokeWidth || 2);
        const hasVariableWidth = object.points.some(p => p.width !== undefined);
        
        // Variable width rendering (pressure-sensitive)
        if (hasVariableWidth && object.points.length >= 2) {
          return (
            <g opacity={drawingOpacity}>
              {object.points.slice(1).map((point, i) => {
                const prev = object.points[i];
                const width = point.width || object.strokeWidth || 2;
                return (
                  <line
                    key={i}
                    x1={prev.x}
                    y1={prev.y}
                    x2={point.x}
                    y2={point.y}
                    stroke={object.stroke}
                    strokeWidth={isHighlighter ? width * 4 : width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              })}
            </g>
          );
        }
        
        if (object.points.length < 3) {
          return (
            <polyline
              points={object.points.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={object.stroke}
              strokeWidth={drawStrokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={drawingOpacity}
            />
          );
        }
        
        // Create smooth path using quadratic beziers
        let drawPathD = `M ${object.points[0].x} ${object.points[0].y}`;
        
        for (let i = 1; i < object.points.length - 1; i++) {
          const p1 = object.points[i];
          const p2 = object.points[i + 1];
          
          const midX = (p1.x + p2.x) / 2;
          const midY = (p1.y + p2.y) / 2;
          
          drawPathD += ` Q ${p1.x} ${p1.y}, ${midX} ${midY}`;
        }
        
        // End at last point
        const lastPt = object.points[object.points.length - 1];
        drawPathD += ` L ${lastPt.x} ${lastPt.y}`;
        
        return (
          <path
            d={drawPathD}
            fill="none"
            stroke={object.stroke}
            strokeWidth={drawStrokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={drawingOpacity}
          />
        );

      case 'image':
        return (
          <image
            href={object.src}
            width={object.width}
            height={object.height}
            preserveAspectRatio="xMidYMid meet"
          />
        );

      case 'arrow':
        // Calculate relative positions within the bounding box
        const relStartX = object.startX - object.x;
        const relStartY = object.startY - object.y;
        const relEndX = object.endX - object.x;
        const relEndY = object.endY - object.y;
        
        // Calculate path based on connector type
        let pathD;
        
        // Curved connector
        if (object.connectorType === 'curved') {
          // Calculate control points for a smooth bezier curve
          const dx = relEndX - relStartX;
          const dy = relEndY - relStartY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const curvature = Math.min(distance * 0.4, 100);
          
          // Adjust control points based on anchor sides for better curves
          let cp1x, cp1y, cp2x, cp2y;
          
          if (object.startSide === 'right') {
            cp1x = relStartX + curvature;
            cp1y = relStartY;
          } else if (object.startSide === 'left') {
            cp1x = relStartX - curvature;
            cp1y = relStartY;
          } else if (object.startSide === 'bottom') {
            cp1x = relStartX;
            cp1y = relStartY + curvature;
          } else if (object.startSide === 'top') {
            cp1x = relStartX;
            cp1y = relStartY - curvature;
          } else {
            cp1x = relStartX + dx * 0.25;
            cp1y = relStartY;
          }
          
          if (object.endSide === 'left') {
            cp2x = relEndX - curvature;
            cp2y = relEndY;
          } else if (object.endSide === 'right') {
            cp2x = relEndX + curvature;
            cp2y = relEndY;
          } else if (object.endSide === 'top') {
            cp2x = relEndX;
            cp2y = relEndY - curvature;
          } else if (object.endSide === 'bottom') {
            cp2x = relEndX;
            cp2y = relEndY + curvature;
          } else {
            cp2x = relEndX - dx * 0.25;
            cp2y = relEndY;
          }
          
          pathD = `M ${relStartX} ${relStartY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${relEndX} ${relEndY}`;
        }
        // Elbow connector - improved smart routing
        else if (object.connectorType === 'elbow' && object.startSide && object.endSide) {
          // Calculate a smarter elbow path that respects connection sides
          // The path should exit from startSide and enter from endSide
          
          const OFFSET = 40; // Distance to travel before turning
          
          // Calculate exit point from start shape
          let exitX = relStartX;
          let exitY = relStartY;
          
          if (object.startSide === 'right') {
            exitX = relStartX + OFFSET;
            exitY = relStartY;
          } else if (object.startSide === 'left') {
            exitX = relStartX - OFFSET;
            exitY = relStartY;
          } else if (object.startSide === 'bottom') {
            exitX = relStartX;
            exitY = relStartY + OFFSET;
          } else if (object.startSide === 'top') {
            exitX = relStartX;
            exitY = relStartY - OFFSET;
          }
          
          // Calculate entry approach point to end shape
          let approachX = relEndX;
          let approachY = relEndY;
          
          if (object.endSide === 'left') {
            approachX = relEndX - OFFSET;
            approachY = relEndY;
          } else if (object.endSide === 'right') {
            approachX = relEndX + OFFSET;
            approachY = relEndY;
          } else if (object.endSide === 'top') {
            approachX = relEndX;
            approachY = relEndY - OFFSET;
          } else if (object.endSide === 'bottom') {
            approachX = relEndX;
            approachY = relEndY + OFFSET;
          }
          
          // Determine if we need intermediate points
          const startHorizontal = object.startSide === 'left' || object.startSide === 'right';
          const endHorizontal = object.endSide === 'left' || object.endSide === 'right';
          
          if (startHorizontal && endHorizontal) {
            // Both horizontal - need vertical connection in middle
            const midX = (exitX + approachX) / 2;
            pathD = `M ${relStartX} ${relStartY} L ${exitX} ${relStartY} L ${midX} ${relStartY} L ${midX} ${relEndY} L ${approachX} ${relEndY} L ${relEndX} ${relEndY}`;
          } else if (!startHorizontal && !endHorizontal) {
            // Both vertical - need horizontal connection in middle
            const midY = (exitY + approachY) / 2;
            pathD = `M ${relStartX} ${relStartY} L ${relStartX} ${exitY} L ${relStartX} ${midY} L ${relEndX} ${midY} L ${relEndX} ${approachY} L ${relEndX} ${relEndY}`;
          } else if (startHorizontal && !endHorizontal) {
            // Start horizontal, end vertical
            pathD = `M ${relStartX} ${relStartY} L ${exitX} ${relStartY} L ${relEndX} ${relStartY} L ${relEndX} ${approachY} L ${relEndX} ${relEndY}`;
          } else {
            // Start vertical, end horizontal
            pathD = `M ${relStartX} ${relStartY} L ${relStartX} ${exitY} L ${relStartX} ${relEndY} L ${approachX} ${relEndY} L ${relEndX} ${relEndY}`;
          }
        }
        
        // Calculate arrow rotation based on end side for proper orientation
        let arrowRotation = 0;
        if (object.endSide === 'left') arrowRotation = 0;      // Arrow pointing right (into left side)
        else if (object.endSide === 'right') arrowRotation = 180; // Arrow pointing left (into right side)
        else if (object.endSide === 'top') arrowRotation = 90;    // Arrow pointing down (into top)
        else if (object.endSide === 'bottom') arrowRotation = -90; // Arrow pointing up (into bottom)
        
        return (
          <g>
            <defs>
              <marker
                id={`arrowhead-${object.id}`}
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill={object.stroke || '#1E293B'} />
              </marker>
            </defs>
            {pathD ? (
              <>
                <path
                  d={pathD}
                  fill="none"
                  stroke={object.stroke || '#1E293B'}
                  strokeWidth={object.strokeWidth || 2}
                  markerEnd={`url(#arrowhead-${object.id})`}
                />
                {/* Invisible wider path for easier selection */}
                <path
                  d={pathD}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={12}
                />
              </>
            ) : (
              <>
                <line
                  x1={relStartX}
                  y1={relStartY}
                  x2={relEndX}
                  y2={relEndY}
                  stroke={object.stroke || '#1E293B'}
                  strokeWidth={object.strokeWidth || 2}
                  markerEnd={`url(#arrowhead-${object.id})`}
                />
                {/* Invisible wider line for easier selection */}
                <line
                  x1={relStartX}
                  y1={relStartY}
                  x2={relEndX}
                  y2={relEndY}
                  stroke="transparent"
                  strokeWidth={12}
                />
              </>
            )}
            {/* Connection indicators */}
            {object.startObjectId && (
              <circle cx={relStartX} cy={relStartY} r={4} fill="#2563EB" />
            )}
            {object.endObjectId && (
              <circle cx={relEndX} cy={relEndY} r={4} fill="#2563EB" />
            )}
          </g>
        );

      default:
        return null;
    }
  };

  return (
    <g
      transform={`translate(${object.x}, ${object.y})`}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onContextMenu={onContextMenu}
      style={{ 
        cursor: object.locked ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
        opacity: object.locked ? 0.7 : 1
      }}
      data-testid={`whiteboard-object-${object.id}`}
    >
      {/* Drop shadow filter */}
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
        </filter>
      </defs>
      
      {renderObject()}
      
      {/* Selection outline */}
      {isSelected && (
        <rect
          x={-2}
          y={-2}
          width={object.width + 4}
          height={object.height + 4}
          fill="none"
          stroke="#2563EB"
          strokeWidth={2}
          strokeDasharray={object.locked ? "4 4" : "none"}
          rx={object.type === 'sticky' ? 6 : 0}
        />
      )}
      
      {/* Lock indicator */}
      {object.locked && (
        <g transform={`translate(${object.width - 16}, -8)`}>
          <circle
            cx={8}
            cy={8}
            r={10}
            fill="#EF4444"
            stroke="white"
            strokeWidth={2}
          />
          <path
            d="M5 8.5v3a1 1 0 001 1h4a1 1 0 001-1v-3M5.5 8.5v-2a2.5 2.5 0 015 0v2"
            fill="none"
            stroke="white"
            strokeWidth={1.2}
            strokeLinecap="round"
            transform="translate(2, 2)"
          />
        </g>
      )}
      
      {renderResizeHandles()}
    </g>
  );
};
